import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  LayoutDashboard,
  Building2,
  Boxes,
  Radio,
  CalendarCheck,
  ClipboardCheck,
  Inbox,
  Star,
  Trophy,
  Megaphone,
  Film,
  Clapperboard,
  LayoutTemplate,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePlatformReviewQueue } from '@/hooks/api/usePlatform';
import { PropertyReviewStatus } from '@pms/shared';
import { cn } from '@/lib/utils';
import { BestAccoAssistant } from '@/components/assistant/BestAccoAssistant';

interface AdminNavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  end?: boolean;
}

const NAV_ITEMS: AdminNavItem[] = [
  { label: 'Overview', path: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Owner Leads', path: '/admin/leads', icon: Inbox },
  { label: 'Groups', path: '/admin/operators', icon: Boxes },
  { label: 'Properties', path: '/admin/properties', icon: Building2 },
  { label: 'Channels', path: '/admin/channels', icon: Radio },
  { label: 'Listing Review', path: '/admin/reviews', icon: ClipboardCheck },
  { label: 'Bookings', path: '/admin/bookings', icon: CalendarCheck },
  { label: 'Guest Reviews', path: '/admin/guest-reviews', icon: Star },
  { label: 'Featured Hotels', path: '/admin/featured', icon: Trophy },
  { label: 'Postings', path: '/admin/postings', icon: Megaphone },
  { label: 'Media Moderation', path: '/admin/media', icon: Film },
  { label: 'Storefront Stories', path: '/admin/storefront-stories', icon: Clapperboard },
  { label: 'Homepage Content', path: '/admin/homepage-content', icon: LayoutTemplate },
];

function AdminNavLink({
  item,
  pendingCount,
  onNavigate,
}: {
  item: AdminNavItem;
  pendingCount?: number | undefined;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.end ?? false}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
          isActive
            ? 'bg-white/15 text-white'
            : 'text-blue-100/80 hover:bg-white/10 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={18} aria-hidden="true" className={cn('shrink-0', isActive ? 'text-white' : 'text-blue-200/70')} />
          <span className="flex-1">{item.label}</span>
          {item.path === '/admin/reviews' && pendingCount != null && pendingCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-[11px] font-semibold text-amber-950">
              {pendingCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function SidebarBody({
  onNavigate,
  onLogout,
  email,
  pendingCount,
}: {
  onNavigate?: () => void;
  onLogout: () => void;
  email: string;
  pendingCount?: number | undefined;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-white/10 shrink-0">
        <Link
          to="/admin"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          aria-label="BestAcco platform console"
        >
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/15 shrink-0">
            <ShieldCheck size={18} className="text-white" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">BestAcco</p>
            <p className="text-[11px] text-blue-200/70 uppercase tracking-widest">Platform Console</p>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav aria-label="Platform navigation" className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV_ITEMS.map((item) => (
          <AdminNavLink key={item.path} item={item} pendingCount={pendingCount} {...(onNavigate ? { onNavigate } : {})} />
        ))}
      </nav>

      {/* User / logout */}
      <div className="shrink-0 border-t border-white/10 p-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full bg-white/15 text-white text-xs font-semibold flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            {email.slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{email}</p>
            <p className="text-xs text-blue-200/70 truncate">Super Admin</p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            title="Sign out"
            aria-label="Sign out"
            className="p-1.5 rounded-lg text-blue-200/70 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: pendingReviews } = usePlatformReviewQueue(PropertyReviewStatus.PENDING_REVIEW);
  const pendingCount = pendingReviews?.length;

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen, closeMobile]);

  const email = user?.email ?? 'admin';

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop sidebar — distinct dark-blue brand, NOT the white hotel shell */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 bg-gradient-to-b from-[#003580] to-[#002255]"
        aria-label="Platform sidebar"
      >
        <SidebarBody onLogout={handleLogout} email={email} {...(pendingCount != null ? { pendingCount } : {})} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden" aria-hidden="true" onClick={closeMobile} />
          <div
            className="fixed inset-y-0 left-0 w-72 max-w-[80vw] z-50 flex flex-col md:hidden bg-gradient-to-b from-[#003580] to-[#002255]"
            role="dialog"
            aria-modal="true"
            aria-label="Platform navigation menu"
          >
            <button
              type="button"
              onClick={closeMobile}
              aria-label="Close navigation menu"
              className="absolute top-3 right-3 p-2 rounded-lg text-blue-200/70 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
            >
              <X size={18} aria-hidden="true" />
            </button>
            <SidebarBody onLogout={handleLogout} email={email} onNavigate={closeMobile} {...(pendingCount != null ? { pendingCount } : {})} />
          </div>
        </>
      )}

      {/* Main content */}
      <main id="main-content" className="flex-1 overflow-y-auto bg-gray-50" tabIndex={-1}>
        {/* Mobile header */}
        <div className="md:hidden flex items-center h-14 px-4 bg-white border-b border-gray-200 sticky top-0 z-10">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <span className="ml-3 font-semibold text-gray-900 text-sm">BestAcco Platform</span>
        </div>

        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Floating conversational assistant — available on every admin page */}
      <BestAccoAssistant variant="admin" />
    </div>
  );
}
